import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import { tap, take, map, mergeMap, mergeAll, debounceTime } from 'rxjs/operators';
import { AnalyticsApiService, BackendService, Dataset, DateRangeValue, DatesService, Match, Schema, SessionService } from '@hub-components';
import { DataSliceEditor, DataSliceViewer, DataSlicersViewData } from '../models/data-slicer.model';
import { Dashboard } from '../models/dashboard.model';
import { FormArray, FormBuilder } from '@angular/forms';

@Injectable({
  providedIn: 'root',
})
export class DataSlicersService {
  private dataSlicersSelectionsMap = new Map<number, any[]>();
  private dataSlicersDimensionsMap = new Map<number, DataSliceViewer[]>();
  private dataSlicersForm = this.fb.array([]);
  private loadedDataSlicersDropdowns: Map<number, Set<number>> = new Map();
  private dataSliceFilterSubject = new Subject<{ filter: string; dataSlice: any; index: number; pageId: number; dateRange: DateRangeValue; shareId?: string; }>();
  private dataSlicersViewData: DataSliceViewer[] = [];
  private dataSlicersViewDataSubject = new BehaviorSubject<DataSlicersViewData>({ pageId: null, dataSlicers: [] });
  private dataSlicersSchemas: { [dataset: string]: Schema } = {};
  private dataSlicersEditorMap = new Map<number, DataSliceEditor[]>();
  private shouldDisableControls = false;

  constructor(
    private analyticsApiService: AnalyticsApiService,
    private backendService: BackendService,
    private sessionService: SessionService,
    private fb: FormBuilder,
    private datesService: DatesService,
  ) {}

  public getDataSlicersSelections(pageId: number): any[] {
    return this.dataSlicersSelectionsMap.get(pageId);
  }

  public setDataSlicersSelections(pageId: number, selections: any[]): void {
    this.dataSlicersSelectionsMap.set(pageId, selections);
  }

  public getDataSlicersDimensions(pageId: number): DataSliceViewer[] | undefined {
    return this.dataSlicersDimensionsMap.get(pageId);
  }

  public setDataSlicersDimensions(pageId: number, dimensions: DataSliceViewer[]): void {
    this.dataSlicersDimensionsMap.set(pageId, [...dimensions]);
  }

  public dataSlicerFilterInit(): Observable<any> {
    return this.dataSliceFilterSubject.pipe(
      debounceTime(500),
      tap(({ filter, dataSlice, index, pageId, dateRange, shareId }) => {
        this.setPageSlicers(pageId, index, true, []);
        this.fetchSliceOptions(dataSlice, index, pageId, dateRange, shareId, filter);
      }),
    );
  }

  public filterDataSlice(filter: string, dataSlice: any, index: number, pageId: number, dateRange: DateRangeValue, shareId?: string): void {
    this.dataSliceFilterSubject.next({ filter, dataSlice, index, pageId, dateRange, shareId });
  }

  public get dataSlicersViewData$(): Observable<DataSlicersViewData> {
    return this.dataSlicersViewDataSubject.asObservable();
  }

  public get dataSlicersFormValue(): any[] {
    return this.dataSlicersForm.getRawValue();
  }

  public get dataSlicersForm$(): FormArray {
    return this.dataSlicersForm;
  }

  public setDataSlicers(dataSlicers: DataSliceEditor[], clearCache: boolean = false): void {
    this.dataSlicersEditorMap.clear();

    if (clearCache) {
      this.loadedDataSlicersDropdowns.clear();
      this.dataSlicersDimensionsMap.clear();
      this.dataSlicersSelectionsMap.clear();
    }

    dataSlicers.forEach(slice => {
      if (!this.dataSlicersEditorMap.has(slice.page)) {
        this.dataSlicersEditorMap.set(slice.page, []);
      }

      const existingSlices = this.dataSlicersEditorMap.get(slice.page);
      existingSlices?.push(slice);
    });
  }

  public getDataSlicers(): DataSliceEditor[] {
    return Array.from(this.dataSlicersEditorMap.values()).flat();
  }

  public getDataSlice(page: number): DataSliceEditor[] | undefined {
    return this.dataSlicersEditorMap.get(page);
  }

  public updateDataSlicers(page: number, dataSlicers: DataSliceEditor[]): void {
    this.dataSlicersEditorMap.set(page, dataSlicers);
    this.loadedDataSlicersDropdowns.delete(page);
    this.dataSlicersDimensionsMap.delete(page);
    this.dataSlicersSelectionsMap.delete(page);
  }

  public resetAllDataSlicerOptionsCache(): void {
    this.loadedDataSlicersDropdowns.clear();

    this.dataSlicersDimensionsMap.forEach((pageSlicers, pageId) => {
      pageSlicers.forEach((_, index) => {
        if (pageSlicers[index]) {
          pageSlicers[index].matches = [];
          pageSlicers[index].loading = false;
        }
      });
      this.setDataSlicersDimensions(pageId, pageSlicers);
    });
  }

  public refreshDataSlicerOptions(pageId: number, dateRange: DateRangeValue, shareId?: string): void {
    this.resetAllDataSlicerOptionsCache();

    const pageSlicers = this.getDataSlicersDimensions(pageId);

    if (pageSlicers?.length) {
      pageSlicers.forEach((slice, index) => {
        if (slice) {
          slice.matches = [];
          slice.loading = true;
          this.fetchSliceOptions(slice, index, pageId, dateRange, shareId);
        }
      });

      this.setDataSlicersDimensions(pageId, pageSlicers);
    }
  }

  public disableDataSlicerControls(): void {
    this.shouldDisableControls = true;
    this.dataSlicersForm.controls.forEach(control => {
      control.disable({ emitEvent: false });
    });
  }

  public enableDataSlicerControls(): void {
    this.shouldDisableControls = false;
    this.dataSlicersForm.controls.forEach(control => {
      control.enable({ emitEvent: false });
    });
  }

  public fetchSliceOptions(dataSlice: DataSliceViewer, index: number, pageId: number, dateRange: DateRangeValue, shareId?: string, filter = ''): void {
    const { startDate, endDate } = this.datesService.parseValue(dateRange);
    if (!this.datesService.isValid(startDate, endDate)) {
      if (pageId !== null && index >= 0) {
        this.setPageSlicers(pageId, index, false, []);
      }
      return;
    }

    const params = { filter };
    if (this.sessionService.publicId) {
      params['public_id'] = this.sessionService.publicId;
    }
    if (shareId) {
      params['share_id'] = shareId;
    }

    params['date_range'] = btoa(JSON.stringify(dateRange));

    this.backendService.load(`data_slicers/${dataSlice.id}/options`, {
      params,
      skipLoader: true,
    }).pipe(
      take(1),
      map((response: any) => response?.matches || []),
      map((rawMatches) => {
        return rawMatches.map((rawMatch) => {
          const match = rawMatch.match;
          const slices = rawMatch.slices;
          const slice = slices[0]['slice'][dataSlice.dimension.name];
          return new Match(slice, match, slices);
        });
      }),
      tap((matches: Match[]) => {
        if (pageId !== null && index >= 0) {
          this.setPageSlicers(pageId, index, false, matches);
        }
      }),
    ).subscribe();
  }

  public loadDataSlicersSchemas(page: number, mergedDatasets: any[], shareId?: string): void {
    this.dataSlicersViewData = [];
    const dataSlicers = this.getDataSlice(page);

    if (!dataSlicers) {
      this.dataSlicersViewDataSubject.next({ pageId: null, dataSlicers: [] });
      return;
    }

    const activeDataSlicers = dataSlicers.filter(slice => !slice._destroy);
    const existingPageData = this.getDataSlicersDimensions(page) || [];

    const initialDataSlicers = activeDataSlicers.map((slice: DataSliceEditor, index: number) => {
      const mergedDataset = mergedDatasets.find(ds => ds.name === slice.dataset);
      const existingSlice = existingPageData[index];

      return new DataSliceViewer({
        id: slice.id,
        dataset: mergedDataset,
        dimension: existingSlice?.dimension || null,
        page: slice.page,
        loading: existingSlice?.matches ? false : true,
        matches: existingSlice?.matches || undefined,
        title: existingSlice?.title || undefined,
      });
    });

    this.dataSlicersViewData = initialDataSlicers;
    this.dataSlicersViewDataSubject.next({
      pageId: page,
      dataSlicers: this.dataSlicersViewData,
    });

    const schemaRequests = activeDataSlicers?.map(slice => {
      const params = {};
      if (this.sessionService.publicId) {
        params['public_id'] = this.sessionService.publicId;
      }
      if (shareId) {
        params['share_id'] = shareId;
      }
      return this.analyticsApiService.loadSchema(slice.dataset, { cache: true, skipLoader: true, params: params }).pipe(
        tap(schema => {
          this.dataSlicersSchemas[slice.dataset] = schema;
        }),
      );
    });

    forkJoin(schemaRequests).pipe(take(1)).subscribe(() => {
      this.processSlicersViewData(page, mergedDatasets);
    });
  }

  public hasDataSlicers(dashboard: Dashboard, currentPageId: number): boolean {
    return dashboard.dataSlicers
      .filter(dataSlicer => !dataSlicer._destroy)
      .some(dataSlicer =>  dataSlicer.page === currentPageId);
  }

  public initializeDataSliceOptions() {
    return this.dataSlicersViewData$
      .pipe(
        tap(({ pageId, dataSlicers }) => {
          if (this.dataSlicersForm.length > 0) {
            this.dataSlicersForm.clear();
          }

          this.setDataSlicersDimensions(pageId, dataSlicers);
        }),
        mergeMap(({ dataSlicers }: DataSlicersViewData) =>
          dataSlicers.map((slice) => {
            const control = this.fb.control([]);

            if (!slice.id || this.shouldDisableControls) {
              control.disable();
            }

            this.dataSlicersForm.push(control);

            return control.valueChanges.pipe(
              map((selectedValues: Match[]) => ({
                slice,
                selectedValues,
              })),
            );
          }),
        ),
        mergeAll(),
      );
  }

  public setSelectionsForm(currentPageId: number, newPageId: number, mergedDatasets: any[], shareId?: string): void {
    const currentPageSelections = this.dataSlicersForm.getRawValue();
    if (currentPageSelections.length) {
      this.setDataSlicersSelections(currentPageId, currentPageSelections);
    }

    this.loadDataSlicersSchemas(newPageId, mergedDatasets, shareId);
    const nextPageSelections = this.getDataSlicersSelections(newPageId) || [];
    nextPageSelections.forEach((value, index) => {
      if (this.dataSlicersForm.controls[index]) {
        this.dataSlicersForm.controls[index].setValue(value, { emitEvent: true });
      }
    });
  }

  public setDataSliceOptions(
    selectedSliceData: DataSliceViewer,
    index: number,
    pageId: number,
    dateRange: DateRangeValue,
    shareId?: string,
  ): void {
    if (!this.loadedDataSlicersDropdowns.has(pageId)) {
      this.loadedDataSlicersDropdowns.set(pageId, new Set());
    }

    const pageDropdowns = this.loadedDataSlicersDropdowns.get(pageId);
    if (pageDropdowns.has(index)) {
      return;
    }

    const pageData = this.getDataSlicersDimensions(pageId);
    if (pageData?.[index]) {
      pageData[index].loading = true;
      this.setDataSlicersDimensions(pageId, pageData);
    }

    this.fetchSliceOptions(selectedSliceData, index, pageId, dateRange, shareId);
    pageDropdowns.add(index);
  }

  private processSlicersViewData(page: number, mergedDatasets: Dataset[]): void {
    const dataSlices = this.getDataSlice(page);
    const activeDataSlices = dataSlices?.filter(slice => !slice._destroy);

    const updatedDataSlicers = activeDataSlices?.map((slice, sliceIndex) => {
      const dataSliceViewer = this.dataSlicersViewData[sliceIndex];

      if (dataSliceViewer) {
        const mergedDataset = mergedDatasets.find(ds => ds.name === slice.dataset);
        const schema = this.dataSlicersSchemas[slice.dataset];
        const dimension = schema.dimensions.find(dim => dim.name === slice.dimension);

        if (dimension) {
          return new DataSliceViewer({
            id: dataSliceViewer.id,
            dataset: dataSliceViewer.dataset,
            dimension: dimension,
            page: dataSliceViewer.page,
            loading: false,
            matches: dataSliceViewer.matches,
            title: `${mergedDataset.displayName} | ${dimension.displayName}`,
          });
        }
      }
      return dataSliceViewer;
    }) || [];

    this.dataSlicersViewData = updatedDataSlicers;

    this.dataSlicersViewDataSubject.next({
      pageId: page,
      dataSlicers: this.dataSlicersViewData
    });
  }

  public saveCurrentFormSelections(pageId: number): void {
    if (this.dataSlicersForm.length > 0) {
      const currentSelections = this.dataSlicersForm.getRawValue();
      this.setDataSlicersSelections(pageId, currentSelections);
    }
  }

  public applySelectionsToForm(pageId: number): void {
    const selections = this.getDataSlicersSelections(pageId) || [];
    selections.forEach((value, index) => {
      if (index < this.dataSlicersForm.controls.length) {
        this.dataSlicersForm.controls[index].setValue(value, { emitEvent: false });
      }
    });
  }

  private setPageSlicers(pageId: number, index: number, loading: boolean, matches: Match[] = []): void {
    const pageSlicers = this.getDataSlicersDimensions(pageId) || [];
    if (pageSlicers[index]) {
      pageSlicers[index].loading = loading;
      pageSlicers[index].matches = matches;
      this.setDataSlicersDimensions(pageId, pageSlicers);
    }
  }
}
